import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { userDtos } from '@vectronic/openApi-customer';
import { ConfirmationDialogComponent } from '@vectronic/ui';
import { DialogService, MembersType, TableCollapseService } from '@vectronic/util';

@Component({
  selector: 'table-member-chips-cell',
  templateUrl: './member-chips-cell.component.html',
  styleUrls: ['./member-chips-cell.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatOptionModule,
    MatAutocompleteModule,
  ],
})
export class MemberChipsCellComponent {
  private dialogService = inject(DialogService);
  private tableCollapseService = inject(TableCollapseService);

  group = input<userDtos.Group>();
  members = input<MembersType>();

  updateGroup = output<userDtos.Group>();

  possibleNewMembers = computed(() =>
    this.members()?.filter(
      ({ id, role }) => !this.group()?.membersIds?.includes(id) && role !== 'READER',
    ),
  );
  filteredMembers = computed(() =>
    this.inputSignal()
      ? this._filter(this.inputSignal(), this.possibleNewMembers())
      : this.possibleNewMembers()?.slice(),
  );
  isReader = computed(() => this.group().role === 'READER');

  separatorKeysCodes: number[] = [ENTER, COMMA];
  memberCtrl = new FormControl('');
  inputSignal = toSignal(this.memberCtrl.valueChanges, { initialValue: null });

  @ViewChild('memberInput') memberInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  remove(memberId: number) {
    const memberName = this.getMemberName(memberId);
    this.dialogService.open(ConfirmationDialogComponent, {
      afterConfirm: ({ toggle }) => this.onRemoveMember(memberId, toggle),
      data: {
        title: `Delete member from group`,
        message: `Are you sure you want to remove ${memberName} from the group?`,
        toggleText: `Remove permission to ${memberName} for any user with a share to this group`,
      },
    });
  }

  selected(event: MatAutocompleteSelectedEvent) {
    const { value } = event.option;
    this.onAddMember(value);
  }

  onAddMember(memberId: number) {
    const newMemberIds = new Set(this.group()?.membersIds);
    newMemberIds.add(memberId);
    const newGroup = { ...this.group(), membersIds: Array.from(newMemberIds) };
    this.updateGroup.emit(newGroup);
  }

  onRemoveMember(memberId: number, removeAccess = false) {
    const newMemberIds = this.group()?.membersIds.filter((id) => id !== memberId);
    const newGroup = { ...this.group(), membersIds: newMemberIds, removeAccess };
    this.updateGroup.emit(newGroup);
  }

  private _filter(value: string, members: MembersType) {
    if (typeof value !== 'string') return members.slice();
    const filterValue = value.toLowerCase();
    return members.filter(({ name }) => name.toLowerCase().includes(filterValue));
  }

  onClickShowMore() {
    if (!this.showMore && !this.isReader()) {
      this.tableCollapseService.setMemberSelectedGroupId(this.group().id);
      setTimeout(() => this.memberInput?.nativeElement?.focus(), 400);
    }
  }

  onClickShowLess(event: MouseEvent) {
    event.stopPropagation();
    this.autocompleteTrigger.closePanel();
    if (this.showMore) this.tableCollapseService.setMemberSelectedGroupId(undefined);
  }

  get showMore() {
    return this.tableCollapseService.isMemberSelectedGroupId(this.group()?.id);
  }

  getMemberName(memberId: number) {
    return this.members().find(({ id }) => id === memberId)?.name;
  }
}
